export type TEventParamValue = string | number | boolean
export type TEventParams = Record<string, TEventParamValue>

/** GA4 limits: 25 parameters per event, 100 characters per string value. */
export const MAX_EVENT_PARAMS = 25
export const MAX_PARAM_LENGTH = 100

/**
 * Last line of defence before a payload reaches GA4: keeps primitives only,
 * drops empty values, truncates strings and caps the parameter count. It
 * does not make an unbounded value bounded — that is the job of the typed
 * events, whose string parameters are unions.
 */
export function sanitiseEventParams(data: unknown): TEventParams | undefined {
	if (!data || typeof data !== "object" || Array.isArray(data)) {
		return undefined
	}
	const params: TEventParams = {}
	let count = 0
	for (const [key, value] of Object.entries(data as Record<string, unknown>)) {
		if (count >= MAX_EVENT_PARAMS) break
		if (value === undefined || value === null) continue
		if (typeof value === "string") {
			params[key] = value.slice(0, MAX_PARAM_LENGTH)
		} else if (typeof value === "number" || typeof value === "boolean") {
			params[key] = value
		} else {
			continue
		}
		count += 1
	}
	return params
}

export type TGAEventParams = {
	event_category?: string
	event_label?: string
	value?: number
}

/**
 * Folds an event's typed parameters into GA4's three general-purpose ones,
 * so the property needs only two custom dimensions (`event_category`,
 * `event_label`) however many events exist; `value` is a built-in metric.
 *
 * The rule, applied in declaration order of the event's parameters:
 * the first string or boolean is the category; every further string or
 * boolean joins the label as `key:value`, except a plain string, which
 * joins as is; the first number is the value, further numbers join the
 * label. `{ method: "whatsapp", content_type: "quotes", content_id:
 * "guest" }` → category `whatsapp`, label `quotes/guest`.
 */
export function foldToGAParams(data: unknown): TGAEventParams | undefined {
	const params = sanitiseEventParams(data)
	if (!params) return undefined
	const folded: TGAEventParams = {}
	const label: string[] = []
	for (const [key, raw] of Object.entries(params)) {
		if (typeof raw === "number") {
			if (folded.value === undefined) {
				folded.value = raw
			} else {
				label.push(`${key}:${raw}`)
			}
			continue
		}
		const text = typeof raw === "boolean" ? `${key}:${raw}` : raw
		if (folded.event_category === undefined) {
			folded.event_category = text
		} else {
			label.push(text)
		}
	}
	if (label.length) {
		folded.event_label = label.join("/").slice(0, MAX_PARAM_LENGTH)
	}
	return folded
}

/** GA4 limit on an event name. */
export const MAX_EVENT_NAME_LENGTH = 40

/**
 * The GA4 event name: the event plus its first parameter — the category
 * `foldToGAParams` picks — so the events report reads `share_whatsapp` or
 * `whatsapp_message_sent_template` without drilling into a dimension. A
 * true boolean appends its key (`login_in_app`), a false one nothing;
 * numbers never join. Lower snake case, cut to GA4's 40 characters.
 */
export function gaEventName(event: string, data: unknown): string {
	const params = sanitiseEventParams(data)
	let suffix = ""
	for (const [key, value] of Object.entries(params || {})) {
		if (typeof value === "number") continue
		if (typeof value === "boolean") {
			suffix = value ? key : ""
		} else {
			suffix = value
		}
		break
	}
	const name = suffix ? `${event}_${suffix}` : event
	return name
		.toLowerCase()
		.replace(/[^a-z0-9_]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, MAX_EVENT_NAME_LENGTH)
}

/**
 * An API path with its identifiers replaced, so one endpoint yields one
 * value however many records it is called for: `/trips/4501/quotes/12` →
 * `/trips/:id/quotes/:id`. Query strings are dropped outright.
 */
export function templateEndpoint(url: string): string {
	const path = url.split("?")[0].split("#")[0]
	return path
		.split("/")
		.map((segment) => (/^\d+$/.test(segment) ? ":id" : segment))
		.join("/")
}

/**
 * A readable, bounded page title derived from a route template — one title
 * per route, whatever the record: `/trips/:tripId/quotes` → "Trips › Quotes",
 * `/trips/:tripId` → "Trips › Details", `/trip-sales-reports` →
 * "Trip Sales Reports". Parameters are dropped except a trailing one, which
 * reads as "Details".
 */
export function humaniseRouteTemplate(template: string): string {
	const segments = template.split("/").filter(Boolean)
	if (!segments.length) return "Home"
	if (segments.length === 1 && segments[0] === "*") return "Not Found"
	const words = segments
		.map((segment, index) => {
			if (segment.startsWith(":") || segment === "*") {
				return index === segments.length - 1 ? "Details" : null
			}
			return segment
				.split(/[-_]+/)
				.filter(Boolean)
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(" ")
		})
		.filter((word): word is string => Boolean(word))
	return words.join(" › ")
}

/**
 * Masks a tenant name for analytics: the first two of every four characters
 * of each word are kept, the rest starred. "Sembark Travels" → "Se**ar* Tr**el*".
 */
export function maskTenantName(name: string): string {
	return name
		.split(" ")
		.map((word) =>
			word
				.split("")
				.map((char, i) => (i % 4 >= 2 ? "*" : char))
				.join("")
		)
		.join(" ")
}
